import {
  digitalSourceTypesDerivedAsFaksimile,
  digitalSourceTypesDerivedAsFotografi,
  digitalSourceTypesDerivedAsGrafikk,
  digitalSourceTypesDerivedAsIllustrasjon,
  digitalSourceTypesDerivedAsKIGenerert,
  IPTC,
} from "bildebanken-model";
import {
  differenceInCalendarDays,
  differenceInCalendarMonths,
  differenceInHours,
  differenceInYears,
  format,
  isFuture,
  isToday,
  isValid,
  isYesterday,
} from "date-fns";
import nbLocale from "date-fns/locale/nb";
import { first, isEqual, last, upperFirst } from "lodash-es";
import type { Readable } from "svelte/store";
import { derived, get } from "svelte/store";

// dateTime on format 1997-12-31T12:12(:12(.000Z))
export function validDate(dateTime: string) {
  const datePart = dateTime.split("T")[0];
  const dateRegex = /^\d{1,4}-\d{2}-\d{2}$/;
  if (!dateRegex.test(datePart)) {
    return false;
  }
  const [year, month, day] = datePart.split("-").map(Number);
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
}
/**
 * Parses an EXIF-style date string ("YYYY:MM:DD HH:mm:ss")
 * into an ISO-8601 date string in UTC.
 *
 * Based on parsing logic from RobG on Stack Overflow:
 * https://stackoverflow.com/a/43084142
 * Returns: new Date(year, month−1, day, hour, minute, second)
 * Then uses Date.prototype.toISOString() (MDN) for formatting.
 */
export function parseExifDateToISO(exifDate: string): string | undefined {
  if (exifDate === "undefined") return; // tests has "undefined" as a string.
  const clean = exifDate.trim().replace(/^"|"$/g, "");

  const parts = clean.split(/\D/);
  // EXIF spec required all fields to be present but could be 0000:00:00 00:00:00 to specify unknown
  // 2019:12:00 00:00:00 is also valid (with unknown date or time) but this will be translated to 2019-01-01 - which is not ideal but best effort for now..
  if (parts.length < 6 || parts[0] === "0000") {
    return;
  }
  const [year, month, day, hour, minute, second] = parts.map(Number);
  const date = new Date(Date.UTC(year, month - 1, day, hour, minute, second));
  return date.toISOString(); // outputs in ISO-8601 UTC format
}

// dateTime on format 1997-12-31T12:12(:12(.000Z))
export function validTime(dateTime: string) {
  const timePart = dateTime.split("T")[1];
  const timeRegex = /^([01]?\d|2[0-3]):[0-5]\d.*$/;
  return timeRegex.test(timePart) || !timePart;
}

export function validDateTime(dateTime: string) {
  return validDate(dateTime) && validTime(dateTime);
}

export function showCount(count: number) {
  if (count >= 10000) return "10k+";
  else if (count >= 1000) return Math.floor(count / 1000) + "k+";
  return count?.toLocaleString("no-nb");
}

export function formatRelativeDate(date: Date, timeZone?: string): string | null {
  if (!date || !isValid(date)) {
    return null;
  }

  if (isFuture(date)) {
    const now = Date.now();
    const hoursFromNow = differenceInHours(date, now);
    const daysFromNow = differenceInCalendarDays(date, now);
    const yearsFromNow = differenceInYears(date, now);
    if (hoursFromNow < 24) {
      return `Om ${hoursFromNow} timer 🛸`;
    } else if (daysFromNow < 1000) {
      return `Om ${daysFromNow} dager 🛸`;
    } else if (yearsFromNow < 100) {
      return `Om ${yearsFromNow.toLocaleString()} år 🛸`;
    } else {
      return "I framtiden 🛸";
    }
  } else if (isToday(date)) {
    return date.toLocaleTimeString("no-NB", { timeStyle: "short", timeZone });
  } else if (isYesterday(date)) {
    return `I går ${date.toLocaleTimeString("no-NB", { timeStyle: "short", timeZone })}`;
  } else if (differenceInCalendarDays(Date.now(), date) < 7) {
    return `${upperFirst(format(date, "eeee", { locale: nbLocale }))} 
      ${date.toLocaleTimeString("no-NB", { timeStyle: "short", timeZone })}`;
  } else if (differenceInCalendarMonths(Date.now(), date) < 6) {
    return format(date, "do MMMM", { locale: nbLocale });
  } else {
    return format(date, "do MMM yyyy", { locale: nbLocale });
  }
}

export function getCurrentDateTime(): string {
  const now = new Date();
  // Adjust to local timezone offset
  const tzOffset = now.getTimezoneOffset() * 60000;
  const localDateTime = new Date(Date.now() - tzOffset)
    .toISOString()
    .slice(0, 16)
    .replace("T", " ");
  return localDateTime;
}
// From https://gist.github.com/lanqy/5193417#gistcomment-3874119
export function bytesToSize(bytes: number, maximumFactionDigits?: number) {
  const units = ["byte", "kilobyte", "megabyte", "gigabyte", "terabyte", "petabyte"];
  const abbreviatedUnit = ["B", "kB", "MB", "GB", "TB", "PB"];
  const unit = Math.floor(Math.log(bytes) / Math.log(1000));
  if (maximumFactionDigits === undefined) {
    maximumFactionDigits = unit > 1 ? 1 : 0;
  }

  try {
    // Modern browser formatting
    return new Intl.NumberFormat(undefined, {
      style: "unit",
      unit: units[unit],
      maximumFractionDigits: maximumFactionDigits,
    }).format(bytes / 1000 ** unit);
  } catch (exception) {
    // Legacy brower fallback (Chrome < 77, Safari < 14.1)
    return new Intl.NumberFormat(undefined, {
      maximumFractionDigits: maximumFactionDigits,
    })
      .format(bytes / 1000 ** unit)
      .concat(" ", abbreviatedUnit[unit]);
  }
}

export function anyValues(object: Record<string, unknown>) {
  return Object.values(object).some((value) => value !== undefined);
}

export function wait(delay: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, delay);
  });
}

export function getErrorMessage(error: unknown): string {
  let message = "Ukjent feil";

  if (
    error &&
    typeof error === "object" &&
    "message" in error &&
    typeof error.message === "string"
  ) {
    message = error.message;
  }

  return message;
}

/**
 * Creates a derived store that only emits new values if the derived value has changed.
 *
 * Comparison of old and new values is done using lodash isEqual.
 *
 * @param store Input store
 * @param map Function to transform store value
 */
export function diffingDerivedStore<T, U>(store: Readable<T>, map: (value: T) => U): Readable<U> {
  let prev = map(get(store));

  return derived(
    store,
    (value, set) => {
      const newValue = map(value);

      if (!isEqual(prev, newValue)) {
        prev = newValue;
        set(newValue);
      }
    },
    prev,
  );
}

export function getEnvironmentFromHostname(hostname: string): string {
  const subdomain = first(hostname.split("."));
  if (!subdomain) return "stage";
  if (subdomain.includes("pr")) return "pr";
  const mapping = { bildebanken: "prod", localhost: "dev" };
  return mapping[subdomain] || last(subdomain.split("-"));
}

export function mapDigitalSourceTypeToImageTypeDescription(
  digitalSourceType: IPTC.DigitalSourceType | undefined,
): string {
  if (digitalSourceTypesDerivedAsFotografi.some((field) => field === digitalSourceType))
    return "Foto: ";
  if (digitalSourceTypesDerivedAsFaksimile.some((field) => field === digitalSourceType))
    return "Faksimile: ";
  if (digitalSourceTypesDerivedAsGrafikk.some((field) => field === digitalSourceType))
    return "Grafikk: ";
  if (digitalSourceTypesDerivedAsIllustrasjon.some((field) => field === digitalSourceType))
    return "Illustrasjon: ";
  if (digitalSourceTypesDerivedAsKIGenerert.some((field) => field === digitalSourceType))
    return "KI-generert: ";
  return "";
}

export function mapDigitalSourceTypeToImageType(
  digitalSourceType: IPTC.DigitalSourceType | undefined,
): string {
  if (digitalSourceTypesDerivedAsFotografi.some((field) => field === digitalSourceType))
    return "Fotografi";
  if (digitalSourceTypesDerivedAsFaksimile.some((field) => field === digitalSourceType))
    return "Faksimile";
  if (digitalSourceTypesDerivedAsGrafikk.some((field) => field === digitalSourceType))
    return "Grafikk";
  if (digitalSourceTypesDerivedAsIllustrasjon.some((field) => field === digitalSourceType))
    return "Illustrasjon";
  if (digitalSourceTypesDerivedAsKIGenerert.some((field) => field === digitalSourceType))
    return "KI-generert";
  console.warn("Feil i kildetype", digitalSourceType);
  return "";
}

export function showAdvancedSourceTypeLabel(
  digitalSourceType: IPTC.DigitalSourceType | undefined,
): string {
  if (!digitalSourceType) return "";
  return `(${digitalSourceType.split("/").slice(-1)[0]})`;
}

export const uuidRe = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/**
 * Strip description of NTB pictures for redundant metadata.
 *
 * @param description Raw description for a NTB picture.
 * @returns Clean description without location, date and byline.
 */
export function stripNtbDescription(description: string): string {
  const locationDateReg = /^(\S+\s+)+\d{8}\./;
  const bylineReg = /Foto:\s?(\S+\s?)+\/\s?\S+\s?$/;

  const stripped = description.replace(locationDateReg, "").replace(bylineReg, "").trim();
  return stripped;
}

export function escapeHtml(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll("'", "&#039;");
}
